import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';

interface FieldEditorProps {
  label: string;
  field: string;
  value: unknown;
  type?: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options?: { value: string; label: string }[];
  canEdit: boolean;
  onSave: (field: string, value: unknown) => void;
  isSaving: boolean;
}

export function FieldEditor({ label, field, value, type = 'text', options, canEdit, onSave, isSaving }: FieldEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));

  function handleSave() {
    let converted: unknown = draft;
    if (type === 'number') converted = Number(draft);
    else if (type === 'boolean') converted = draft === 'true';
    else if (type === 'date' && draft === '') converted = null;

    onSave(field, converted);
    setIsEditing(false);
  }

  function handleCancel() {
    setDraft(String(value ?? ''));
    setIsEditing(false);
  }

  function formatDisplay(): string {
    if (value == null || value === '') return '—';
    if (type === 'number' && typeof value === 'number') {
      return value.toLocaleString('nb-NO');
    }
    if (type === 'boolean') return value ? 'Ja' : 'Nei';
    if (type === 'date') {
      return new Date(String(value)).toLocaleDateString('nb-NO');
    }
    return String(value);
  }

  if (!isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1,
        }}
      >
        <Typography component="dt" variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        <Box component="dd" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
          <Typography variant="body2" color="text.primary">
            {formatDisplay()}
          </Typography>
          {canEdit && (
            <Button
              onClick={() => setIsEditing(true)}
              size="small"
              variant="text"
              aria-label={`Rediger ${label}`}
              sx={{ minWidth: 0, p: 0, fontSize: '0.75rem' }}
            >
              Rediger
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}>
      <Typography
        component="label"
        variant="body2"
        color="text.secondary"
        fontWeight={500}
        sx={{ display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {type === 'select' && options ? (
          <Select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          >
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        ) : type === 'boolean' ? (
          <Select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          >
            <MenuItem value="true">Ja</MenuItem>
            <MenuItem value="false">Nei</MenuItem>
          </Select>
        ) : (
          <TextField
            type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={type === 'number' ? { step: 'any' } : undefined}
            size="small"
            sx={{ flex: 1 }}
            autoFocus
          />
        )}
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          size="small"
        >
          {isSaving ? '...' : 'Lagre'}
        </Button>
        <Button
          onClick={handleCancel}
          variant="outlined"
          size="small"
        >
          Avbryt
        </Button>
      </Box>
    </Box>
  );
}
